import React, {lazy, useEffect, useState} from "react";
import {
    checkNullArr,
    checkNullJson,
    checkNullStr,
    formatDate,
    getArrLen,
    getDefStr,
    getJsonValue,
    isBoolTrue,
    isJsonValueTrue,
    isValueBool,
    parseInteger,
} from "../../common/fusion-ui/utils/ReactUtils";
import {DataGrid, GridToolbar} from '@mui/x-data-grid';
import {makeStyles} from "@mui/styles";
import {createTheme} from "@mui/material/styles";
import {AdminModuleFieldsData, AdminModuleFormReqUrls, AdminModules, AdminModuleTableCellType,} from "../config/AdminConfig";
import {Anchor, Avatar, Flexbox, Label} from "../../common/fusion-ui";
import {Constants, CssVariant, DefStrNA, IconClass, RippleVarVariant} from "../../common/fusion-ui/config/FusionConfig";
import {getAdminSessionField, sendAdminRequest} from "../utils/AdminUtils";
import {formatDateValue, printError, printLog} from "../../common/CommonUtils";
import {useToast} from "../../common/fusion-ui/provider/ToastProvider";
import {ThemedButton, ThemedIconButton} from "../../common/components";
import {useTheme} from "../../common/ProjectThemeProvider";
import {TableSkeleton} from "./index";
import {ThemeProvider} from "@mui/material";
import {AppDateFormat} from "../../common/CommonConfig";
import {ShowViewType} from "../../app/config/AppConfig";
import useScreenSize from "../../common/fusion-ui/hook/useScreenSize";

//Lazy imports (to reduce load)
const ParentCompany__AddEditFormPage = lazy(() => import('../forms/ParentCompany__AddEditFormPage'));
const Group__AddEditFormPage = lazy(() => import('../forms/Group__AddEditFormPage'));
const Country__AddEditFormPage = lazy(() => import('../forms/Country__AddEditFormPage'));
const State__AddEditFormPage = lazy(() => import('../forms/State__AddEditFormPage'));
const City__AddEditFormPage = lazy(() => import('../forms/City__AddEditFormPage'));
const ExpSpeaks__AddEditFormPage = lazy(() => import('../forms/ExpSpeaks__AddEditFormPage'));
const LoginBypassManager__EditFormPage = lazy(() => import('../forms/LoginBypassManager__EditFormPage'));

const TableDensityMode = {
    compact: "compact", comfortable: "comfortable", standard: "standard",
};

const useTableStyles = makeStyles((theme) => ({
    grid: {
        // display: "flex", // flexDirection: "column-reverse"
    }, menu: {
        // backgroundColor: 'red !important',
        // background: 'white !important',
        // boxShadow: 'var(--shadowMd) !important',
        // borderRadius: '2rem !important',
        // overflow: 'hidden !important',
    }, menuItem: {
        // backgroundColor: 'yellow !important',
        // fontSize: '12px !important',
        // color: 'black !important',
        // padding: '.2rem .4rem !important',
    },
}));

const tableStylesTheme = createTheme({
    components: {
        MuiInputLabel: {
            styleOverrides: {
                asterisk: {
                    color: "#F70000",
                },
            },
        },
    }, Button: {
        styleOverrides: {
            textTransform: "capitalize", backgroundColor: "red",
        },
    }, columnHeaderHeight: {
        styleOverrides: {
            minHeight: "100px", height: "100px", backgroundColor: "green !important",
        },
    },
});


/**
 * The ModuleTable component is a memoized React component designed
 * to generate a table layout for administrative modules.
 * - It uses AdminConfig.{@link AdminModuleFieldsData} or specified columnModel to populate rows & columns.
 * - It fetches configuration and data either from AdminModuleFieldsData or a
 * specified columnModel to populate the rows and columns.
 * - It takes several props including moduleName, showLoading, columns, and event callbacks
 * and leverages hooks to manage state and side effects.
 * The component decides between showing a loading skeleton or rendering the table
 * with its rows and columns based on the loading state.
 * - It also provides functionality for rendering different types of cells within the table,
 * such as link, email, mobile, numeric, date, image, status, and action cells.
 * - The table supports pagination, sorting, column visibility toggling, and export functionalities.
 * - Additionally, it includes embedded forms for adding/editing records based on the modules,
 * and confirmation dialogs for specific actions.
 *
 * @param {Object} props
 * @param {string} props.moduleName - The name of the module, used for setting titles and API endpoints.
 * @param {string} props.showLoading -  Whether to show loading-skeleton.
 * @param {boolean} props.showMultipleDelete -  Whether to show multiple-delete button.
 * @param {function} props.onValueChange -  Callback function to handle state changes when table values are updated.
 * @param {Object} props.columnModel -  Custom column-model to be used.
 *
 * @example
 * <ModuleTable
 *   moduleName={moduleName}
 *   showMultipleDelete={false}
 *   onValueChange={(updateRows, triggerRefresh)=>{console.log('updateRows:',updateRows,'triggerRefresh:',triggerRefresh)}}>
 *   {tableRows}
 *  </ModuleTable>
 */
const ModuleTable = React.memo(({
                                    moduleName, showLoading = false, onValueChange, children, columnModel,//={}
                                    showMultipleDelete = false,
                                }) => {
    const TAG = 'ModuleTable';

    const theme = useTheme();
    const {showToast, changeToastVariant} = useToast();
    const {isScreenUpSm} = useScreenSize();

    const NA = DefStrNA;
    const TABLE_HEIGHT = '75vh';
    const GEN_CELL_CLS = `m-0 p-1 px-2 text-wrap fs-sm table-cell`;
    const ALIGN_AT = "start";
    const TABLE_ROWS_LIMIT_OPTIONS = [10, 25, 50, 100];
    const TABLE_ROWS_LIMIT = TABLE_ROWS_LIMIT_OPTIONS[2];
    const RECORD_DISABLE_CONFIRM_MSG = "Are you sure that you want to disable this record?";
    const RECORD_DELETE_CONFIRM_MSG = "Are you sure that you want to delete this record (no recovery)?";

    const [tableRows, setTableRows] = useState([]);
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [moduleColumns, setModuleColumns] = useState({});
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

    const [showLoader, setShowLoader] = useState(isBoolTrue(showLoading));
    const [disableControls, setDisableControls] = useState(false);
    const [showStatusActionEditLoader, setShowStatusActionEditLoader] = useState(false);
    const [statusActionEditRecordId, setStatusActionEditRecordId] = useState(null);

    const [formPopupData, setFormPopupData] = useState({recordId: null, showPopup: false});

    const tableClasses = useTableStyles();

    useEffect(() => {
        try {
            // log('useEffect.moduleName:', moduleName, 'showLoading:', showLoading, "children:", children);
            setShowLoader(isBoolTrue(showLoading));
            setTableRows(children);
            const moduleColumns = checkNullJson(columnModel) ? columnModel : AdminModuleFieldsData[moduleName];
            setModuleColumns(moduleColumns);
            setColumnVisibilityModel(getColVisModel(moduleColumns));
        } catch (e) {
            logErr(e);
            setShowLoader(false);
        }
    }, [moduleName, children, showLoading, columnModel]);

    return (<>
        <div className={`module-tabular-view`}>
            {showLoader ? <TableSkeleton/> : <div className={"p-0 m-0 w-100"}>{renderTable()}</div>}
        </div>

        {/*popups*/}
        {renderAddEditForms()}
    </>);

    function renderTable() {
        return <div className={'w-100'}>
            <div
                className={"scrollerx scroller-autox scroll-darkx bg-infox w-100x"}
                style={{height: TABLE_HEIGHT, maxWidth: ''}}
            >
                <ThemeProvider theme={tableStylesTheme}>
                    <DataGrid
                        className={'w-90x scrollerx'}
                        columns={getModuleColsList(moduleColumns).map((colField, index) => {
                            const colData = colField[Object.keys(colField)[0]]; //get column name from column's json-object
                            if (isBoolTrue(colData.show)) {
                                return {
                                    field: colData.title,
                                    headerName: colData.title,
                                    renderHeader: () => (<label className={`p-0 py-1 m-0 fs-sm fw-semi-bold header-cell text-center select-none`}>
                                        {colData.title}
                                    </label>),
                                    sortable: isBoolTrue(colData.sortable),
                                    hideable: isBoolTrue(colData.hideable),
                                    exportable: isBoolTrue(colData.exportable), // editable: true,
                                    flex: 1, // minWidth: 300,
                                    // width: 300,
                                    minWidth: colData.width,
                                    renderCell: (params) => renderTableCell(params, colData.title, colData.cellType, index, colData),
                                };
                            }
                        }).filter(item => !isValueBool(item) && checkNullJson(item))}
                        rows={tableRows}
                        getRowId={(row) => row.recordId}
                        // disableAutosize={true}
                        // showLoader={showLoader}
                        classes={{
                            menu: tableClasses.menu, menuItem: tableClasses.menuItem,
                        }}
                        onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
                        columnVisibilityModel={columnVisibilityModel}
                        pagination
                        // autoPageSize={true}
                        pageSizeOptions={TABLE_ROWS_LIMIT_OPTIONS}
                        // rowCount={TABLE_ROWS_LIMIT}
                        density={TableDensityMode.comfortable}
                        // noRowsOverlay={TableOverlay}
                        // slots={{ loadingOverlay: () => ( <div> <CircularProgress /> <span>Loading...</span> </div> ), }}
                        // styleOverrides={{ '.MuiDataGrid-noRowsOverlay': { display: 'none', }, }}
                        // slots={{ noRowsOverlay: () => TableOverlay}}
                        sx={getModuleTableStyle()}
                        // checkboxSelectionVisibleOnly={true}//select/deselect only visible-rows
                        disableRowSelectionOnClick
                        // isRowSelectable={(params) => params.row.quantity > 50000}
                        checkboxSelection
                        onRowSelectionModelChange={(selectedRowIds) => {
                            const fun = 'handleRowSelectionChange:';
                            // log(fun, 'selectedRowIds:', selectedRowIds);
                            setSelectedRowIds(selectedRowIds);
                            if (checkNullArr(selectedRowIds)) {
                                try {
                                    const updatedRows = tableRows.filter(row => selectedRowIds.includes(parseInteger(row.recordId)));
                                    // log(fun, 'updatedRows:', updatedRows);
                                    if (onValueChange) onValueChange(updatedRows);
                                } catch (error) {
                                    logErr(fun, error);
                                }
                            }
                        }}
                        components={{
                            Toolbar: GridToolbar, // Toolbar: <GridToolbarExport csvOptions={{fileName: moduleName, delimiter: ';', utf8WithBom: true,}}/>
                            // FilterPanel: GridFilterPanel,
                            // ColumnsPanel: GridColumnsPanel,
                            // Pagination: GridPagination,
                            // PreferencesPanel: GridPreferencesPanel,
                            // Cell: GridCell,
                            // SkeletonCell: GridSkeletonCell,
                            // ColumnHeaderFilterIconButton: GridColumnHeaderFilterIconButton,
                            // ColumnMenu: GridColumnMenu,
                            // Footer: GridFooter,
                            // ColumnHeaders: GridColumnHeaders,
                            // LoadingOverlay: GridLoadingOverlay,
                        }}
                        // slots={{toolbar: GridToolbar}}
                        slotProps={{
                            toolbar: {
                                csvOptions: {fileName: getCsvExportName()}, printOptions: {fileName: getCsvExportName()},
                            }
                        }}
                    />
                </ThemeProvider>
            </div>

            {/*footer*/}
            {(isBoolTrue(showMultipleDelete)) && <Flexbox className={'mt-2'} justifyAt={'start'} alignAt={'start'}>
                {isBoolTrue(showMultipleDelete) && <ThemedButton
                    className={"px-2 py-1 fs-sm w-auto"}
                    text={"Delete"}
                    icon={IconClass.delete}
                    // disabled={tableRows.every(row => selectedRowIds.includes(parseInteger(row.recordId)) && !isJsonValueTrue(row, 'isActive'))}
                    disabled={disableControls || !checkNullArr(tableRows)}
                    height={"28px"}
                    outlined={true}
                    variant={'red'}
                    onClick={handleMultipleRecordsEnableDisable}
                />}
            </Flexbox>}
        </div>
    }

    /**
     * Render table cell based on {@link AdminModuleTableCellType};
     */
    function renderTableCell(rowParams, colTitle, cellType, rowIndex, colData) {
        const row = rowParams.row;
        const cellValue = row[colTitle] + '';
        switch (cellType) {
            case AdminModuleTableCellType.LINK:
                return renderLinkCell(cellValue)
            case AdminModuleTableCellType.EMAIL:
            case AdminModuleTableCellType.MOBILE:
                return checkNullStr(cellValue) ? <Anchor
                    className={`text-break text-break-space ${GEN_CELL_CLS}`}
                    href={`${cellType == AdminModuleTableCellType.EMAIL ? 'mailto' : 'tel'}:${cellValue}`}
                    simple={true}>{cellValue}</Anchor> : renderNaCell()
            case AdminModuleTableCellType.NUMBER:
                return <Label className={GEN_CELL_CLS} textAt={ALIGN_AT}>
                    {checkNullStr(cellValue) ? parseInt(cellValue) : NA}
                </Label>
            case AdminModuleTableCellType.DATE:
                return <Label className={`${GEN_CELL_CLS} text-nowrapx`} textAt={ALIGN_AT}>
                    {formatDateValue(cellValue, AppDateFormat)}
                </Label>
            case AdminModuleTableCellType.DATE_SHORT:
                return <Label className={`${GEN_CELL_CLS} text-nowrapx`} textAt={ALIGN_AT}>
                    {formatDateValue(cellValue, Constants.DD_MMM_YYYY_DASH)}
                </Label>
            case AdminModuleTableCellType.IMAGE:
                return renderImageCell(row, cellValue, colData);
            case AdminModuleTableCellType.IMAGE_CIRCULAR:
                return renderCircularImageCell(cellValue, true, colData);
            case AdminModuleTableCellType.IMAGE_SQUARE:
                return renderCircularImageCell(cellValue, false, colData);
            case AdminModuleTableCellType.YES_NO:
                return renderYesNoCell(cellValue);
            case AdminModuleTableCellType.YES_NO_INT:
                return renderYesNoCell(cellValue, true);
            case AdminModuleTableCellType.YES_NO_TAG:
                return renderYesNoTagCell(cellValue);
            case AdminModuleTableCellType.YES_NO_INT_TAG:
                return renderYesNoTagCell(cellValue, true);
            case AdminModuleTableCellType.YES_NO_TAG_REVERSE:
                return renderYesNoTagCell(cellValue, false, true);
            case AdminModuleTableCellType.YES_NO_INT_TAG_REVERSE:
                return renderYesNoTagCell(cellValue, true, true);
            case AdminModuleTableCellType.STATUS_YN:
                return renderStatusYnControlBoxForCell(row);
            case AdminModuleTableCellType.STATUS_EDIT_ENABLE_DISABLE_ACTIONS:
                return renderStatusActionCell__enableDisableToggle_EditControls(row);
            case AdminModuleTableCellType.STATUS_ENABLE_DISABLE_ACTIONS:
                return renderStatusActionCell__enableDisableToggle_EditControls(row, true, false);

            //specific-custom cells
            case AdminModuleTableCellType.MEMBER_ACTION__EDIT_DELETE:
                return renderActionCell__memberEditDelete(row);
            case AdminModuleTableCellType.GROUPS_MEMBS_ACTION__EDIT:
                return renderActionCell__groupEdit(row);
            case AdminModuleTableCellType.LOGIN_BPS_ACTION__EDIT:
                return renderActionCell__login_bps(row);
            default:
                return <p className={`text-break text-break-space m-0 p-0 ${GEN_CELL_CLS}`}>{getDefStr(cellValue, NA)}</p>
        }
    }

    function renderLinkCell(cellValue) {
        return (checkNullStr(cellValue)) ? <Anchor className={`text-break text-break-space ${GEN_CELL_CLS}`} href={cellValue}
                                                   textAt={ALIGN_AT} target={"_blank"}>{cellValue}
        </Anchor> : renderNaCell()
    }

    function renderStatusActionCell__enableDisableToggle_EditControls(row, showEnableDisable = true, showEdit = true) {
        return <div
            className={`m-0 p-0 w-100 d-flex jc-${ALIGN_AT} al-center flex-nowrap ${GEN_CELL_CLS}`}>
            <div className={'me-n1'}>{renderStatusYnControlBoxForCell(row)}</div>
            {(showEdit || showEnableDisable) && <div className={'select-none text-theme-resp-25 px-1'}>|</div>}
            {showEnableDisable && renderCellEnableDisableToggleControl({row: row, ml: 0})}
            {showEdit && renderCellEditControl({row: row, ml: 1})}
        </div>
    }


    function renderActionCell__memberEditDelete(row) {
        return <div
            className={`m-0 p-0 w-100 d-flex jc-${ALIGN_AT} al-center flex-nowrap ${GEN_CELL_CLS}`}>
            <div className={'me-n1'}>{renderStatusYnControlBoxForCell(row)}</div>
            <div className={'select-none text-theme-resp-25 px-1'}>|</div>
            {renderCellEnableDisableToggleControl({row: row, ml: 0})}
            {renderCellEditControl({row: row, ml: 1})}

            {/*clone-data*/}
            <ThemedIconButton
                className={""}
                tooltip={"Clone Member"}
                ml={1}
                icon={IconClass.copy}
                circular={true}
                disabled={disableControls}
                iconSize={"13px"}
                size={"28px"}
                bgColor={'transparent'}
                hoverBgColor={theme.primary10}
                color={theme.text}
                hoverColor={theme.primary}
                rippleColor={theme.primary40}
                href={`${AdminModuleFormReqUrls[moduleName].clone}${row.recordId}`}
            />
        </div>
    }

    function renderActionCell__login_bps(row) {
        return <div
            className={`m-0 p-0 w-100 d-flex jc-${ALIGN_AT} al-center flex-nowrap ${GEN_CELL_CLS}`}>
            <div className={'me-n1'}>{renderStatusYnControlBoxForCell(row)}</div>
            <div className={'select-none text-theme-resp-25 px-1'}>|</div>
            {renderCellEnableDisableToggleControl({row: row, ml: 0})}
            {renderCellEditControl({row: row, ml: 1})}
        </div>
    }

    function renderActionCell__groupEdit(row) {
        return <div
            className={`m-0 p-0 w-100 jc-${ALIGN_AT} al-center d-flex flex-nowrap ${GEN_CELL_CLS}`}>
            {/*edit-btn*/}
            {/*<ThemedIconButton*/}
            {/*    className={""}*/}
            {/*    tooltip={"Edit Members"}*/}
            {/*    icon={IconClass.plusCircle}*/}
            {/*    circular={true}*/}
            {/*    disabled={disableControls}*/}
            {/*    iconSize={"16px"}*/}
            {/*    size={"28px"}*/}
            {/*    bgColor={'transparent'}*/}
            {/*    hoverBgColor={theme.primary10}*/}
            {/*    color={theme.text}*/}
            {/*    hoverColor={theme.primary}*/}
            {/*    href={`${AdminModuleFormReqUrls[moduleName].editGroupMembers}${row.recordId}`}*/}
            {/*/>*/}
            <ThemedButton
                className={"px-2 py-1 fs-sm"}
                text={"Add/Edit Members"}
                // icon={IconClass.plusCircle}
                disabled={disableControls}
                height={"28px"}
                outlined={true}
                variant={theme.primary}
                bgColor={theme.primary05}
                href={`${AdminModuleFormReqUrls[moduleName].editGroupMembers}${row.recordId}`}
            />
        </div>
    }

    function renderCellEditControl({row, ml = 0}) {
        const editUrl = `${AdminModuleFormReqUrls[moduleName].edit}${row.recordId}`;
        return <ThemedIconButton
            className={""}
            tooltip={"Edit"}
            icon={IconClass.edit}
            circular={true}
            ml={ml}
            disabled={disableControls}
            iconSize={"13px"}
            size={"28px"}
            bgColor={'transparent'}
            hoverBgColor={theme.primary10}
            color={theme.text}
            hoverColor={theme.primary}
            rippleColor={RippleVarVariant.primary}
            href={editUrl}
            onClick={(e) => {
                //Show popup for some specific module-forms
                if (!isScreenUpSm && [AdminModules.ParentCompany, AdminModules.Groups, AdminModules.Country, AdminModules.State, AdminModules.City, AdminModules.ExpertSpeaks, AdminModules.LoginBypassManager].includes(moduleName)) {
                    e.preventDefault();
                    if (e.ctrlKey) {
                        window.open(editUrl, '_blank');
                    } else {
                        setFormPopupData({recordId: row.recordId, showPopup: true});
                    }
                }
            }}
        />
    }

    function renderCellEnableDisableToggleControl({row, ml = 1}) {
        const isActive = isBoolTrue(row.isActive);
        const recordId = row.recordId;
        return <ThemedIconButton
            className={""}
            tooltip={disableControls ? '' : (isActive ? "Disable" : "Enable")}
            iconClass={isActive ? IconClass.closeCircle : IconClass.checkCircle}
            circular={true}
            ml={ml}
            disabled={disableControls}
            iconSize={"14px"}
            size={"28px"}
            asLoading={statusActionEditRecordId == recordId && showStatusActionEditLoader}
            loadingWidth={'28px'}
            noCursorOnLoading={true}
            textOnLoading={false}
            loadingSizeVariant={'sm'}
            bgColor={'transparent'}
            hoverBgColor={isActive ? 'var(--danger15)' : theme.text10}
            color={isActive ? 'var(--danger75)' : theme.text}
            hoverColor={isActive ? 'red' : theme.text}
            rippleColor={isActive ? RippleVarVariant.danger : RippleVarVariant.dark}
            onClick={() => {
                if (isActive) {
                    if (window.confirm(RECORD_DISABLE_CONFIRM_MSG)) {
                        handleRecordEnableDisable(recordId, isActive);
                    }
                } else {
                    handleRecordEnableDisable(recordId, isActive);
                }

            }}
        />
    }

    function renderYesNoCell(cellValue, checkInt = false) {
        return <div className={`m-0 m-0 w-100 jc-${ALIGN_AT} al-center d-flex flex-nowrap ${GEN_CELL_CLS}`}>
            <label
                className={``}>
                {(isBoolTrue(cellValue, true, true) || (!checkInt || (checkInt && parseInteger(cellValue) > 0))) ? `YES` : "NO"}
            </label>
        </div>
    }

    function renderYesNoTagCell(cellValue, checkInt = false, reverse = false) {
        const isYes = isBoolTrue(cellValue, true, true) || (checkInt && parseInteger(cellValue) > 0);
        return <div className={`m-0 m-0 w-100 jc-${ALIGN_AT} al-center d-flex flex-nowrap ${GEN_CELL_CLS}`}>
            <label
                className={`d-flex text-center-force al-center jc-center rounded-10 text-white fs-md-sm fw-semi-bold ${isYes ? (reverse ? 'bg-danger' : 'bg-primary') : (reverse ? 'bg-primary' : 'bg-danger')}`}
                style={{padding: '.15rem .6rem'}}>
                {isYes ? `YES` : "NO"}
                {/*{' | ' + isYes}*/}
                {/*{' | ' + jsonToStr(cellValue)}*/}
            </label>
        </div>
    }

    function renderStatusYnControlBoxForCell(row) {
        const isRecordActive = isBoolTrue(row.isActive);
        return <div className={`m-0 m-0 w-100 jc-${ALIGN_AT} al-center d-flex flex-nowrap ${GEN_CELL_CLS}`}>
            <label
                className={`px-2 py-1x m-0 w-auto text-center-force d-flex jc-center fs-md-sm al-center flex-nowrap rounded-2 fw-semi-bold select-none ${isRecordActive ? `bg-success-20 text-success` : `bg-danger-20 text-danger`}`}
                style={{height: '18px'}}>
                {isRecordActive ? `YES` : "NO"}
                {/*{isRecordActive ? `Active` : "Inactive"}*/}
            </label>
        </div>
    }

    function renderImageCell(row, cellValue, colData) {
        return checkNullStr(cellValue) ? <div className={`m-0 m-0 w-100 jc-center al-center d-flex flex-nowrap ${GEN_CELL_CLS.replace('px-2', 'px-1')} ms-n1x`}>
            <Avatar
                wrapperClassName={''}
                imgClassName={''}
                width={checkNullStr(colData.width) ? `${colData.width}px` : '180px'}
                height={'auto'}
                size={''}
                circular={false}
                bordered={false}
                showFullPreviewDialog={true}
            >{cellValue}</Avatar>
        </div> : renderNaCell(ALIGN_AT);
    }

    function renderCircularImageCell(cellValue, circular = false, colData) {
        return checkNullStr(cellValue) ? <div className={`m-0 m-0 w-100 jc-center al-center d-flex flex-nowrap ${GEN_CELL_CLS.replace('px-2', 'px-1')} ms-n2`}>
            <Avatar
                wrapperClassName={''}
                imgClassName={`${isBoolTrue(circular) ? 'circular' : 'rounded-2'}`}
                size={checkNullStr(colData.width) ? `${parseInteger(colData.width)}px` : '50px'}
                // height={checkNullStr(colData.width) ? `${parseInteger(colData.width)}px` : '50px'}
                // width={'40px'}
                // height={'40px'}
                circular={false}
                bordered={false}
                showFullPreviewDialog={true}
            >{cellValue}</Avatar>
        </div> : renderNaCell(ALIGN_AT);
    }

    function renderNaCell() {
        return <Label className={GEN_CELL_CLS} textAt={ALIGN_AT}>
            {NA}
        </Label>
    }

    function renderAddEditForms() {
        switch (moduleName) {
            case AdminModules.ParentCompany:
                return <ParentCompany__AddEditFormPage{...__createProps()}/>
            case AdminModules.Groups:
                return <Group__AddEditFormPage{...__createProps()}/>
            case AdminModules.Country:
                return <Country__AddEditFormPage{...__createProps()}/>
            case AdminModules.State:
                return <State__AddEditFormPage{...__createProps()}/>
            case AdminModules.City:
                return <City__AddEditFormPage{...__createProps()}/>
            case AdminModules.ExpertSpeaks:
                return <ExpSpeaks__AddEditFormPage{...__createProps(ShowViewType.DRAWER)}/>
            case AdminModules.LoginBypassManager:
                return <LoginBypassManager__EditFormPage{...__createProps()}/>
            // case AdminModules.GroupsMembers:
            //     return <Group__AddEditMembersFormPage{...__createProps()}/>
            default:
                return null;
        }

        function __createProps(showAs = ShowViewType.DIALOG) {
            return {
                showAs: showAs, updateOnly: true, recordId: formPopupData.recordId, open: formPopupData.showPopup, onSuccess: (() => {
                    setFormPopupData({recordId: null, showPopup: false});
                    if (onValueChange) onValueChange(tableRows, true);//2nd param: trigger table-refresh
                }), onHide: (() => setFormPopupData({recordId: null, showPopup: false})),
            }
        }

    }

    /**
     * Handles enabling or disabling a record based on its current status.
     *
     * This function sends a request to the server with the specified recordId
     * and the desired status (isActive), and updates the records in the table.
     *
     * @param {string|number} recordId - The ID of the record to be enabled/disabled.
     * @param {boolean} isActive - The current active status of the record.
     * @param {boolean} [multiple = false] - Whether the operation should be performed on multiple records.
     * @param {boolean} [triggerCallback = true] - Whether the callback function should be triggered upon success.
     * @param {boolean} [triggerRefresh = false] - Whether to refresh the table when the callback function is triggered upon success.
     * @param {object} [reqOptions = {}] - Request options.
     */
    function handleRecordEnableDisable(recordId, isActive, multiple = false, triggerCallback = true, triggerRefresh = false, reqOptions = {}) {
        const fun = 'handleRecordEnableDisable:';
        try {
            let reqUrl = AdminModuleFormReqUrls[moduleName].delete;
            log('moduleName:', moduleName, 'recordId:', recordId, 'reqUrl:', reqUrl);
            if (!checkNullStr(reqUrl)) {
                toast("[DEBUG] Request-URL not found!", CssVariant.warning);
                return false;
            }

            const REQUEST_OPTIONS = {
                id: recordId, isActive, multiple,
            };

            const proceed = true;
            // const proceed = false;
            if (proceed) {
                setDisableControls(true);
                if (!isBoolTrue(multiple)) setStatusActionEditRecordId(recordId);
                setShowStatusActionEditLoader(true);

                sendAdminRequest({
                    reqUrl: reqUrl, reqOptions: REQUEST_OPTIONS, onFetched: ((fetchedData) => {
                        const message = getJsonValue(fetchedData, 'message');
                        const success = isJsonValueTrue(fetchedData, 'success');
                        log(fun, 'fetchedData:', fetchedData, 'message:', message, 'success:', success);

                        if (success) {
                            toast(message, isActive ? CssVariant.info : CssVariant.success);

                            //update curr row
                            const updatedRows = tableRows.map((item) => {
                                if (isBoolTrue(multiple)) {

                                } else {
                                    if (item.recordId == recordId) {
                                        return {...item, isActive: !isActive};
                                    }
                                }
                                return item;
                            });
                            // log(fun, 'updatedRows:', updatedRows);
                            setTableRows(updatedRows);
                            if (isBoolTrue(triggerCallback)) {
                                if (onValueChange) onValueChange(updatedRows, triggerRefresh);
                            }
                        } else {
                            toast(message, CssVariant.danger);
                        }
                        setDisableControls(false);
                        setShowStatusActionEditLoader(false);
                    }), onError: ((err) => {
                        toast('Operation failed, please try again!', CssVariant.danger);
                        logErr(fun, err);
                        setDisableControls(false);
                        setShowStatusActionEditLoader(false);
                    }),
                });
            } else {
                toast('Operation disabled!', CssVariant.warning);
            }
        } catch (err) {
            toast('Operation failed, please try again!', CssVariant.danger);
            logErr(fun, err);
            setDisableControls(false);
            setShowStatusActionEditLoader(false);
        }
    }

    /**
     * Handles the enabling or disabling of multiple records based on the selected rows.
     * <p>
     * This function checks if any rows are selected. If so, it confirms with the user,
     * and then calls the {@link handleRecordEnableDisable} function to disable the records.
     * <p>
     * It also displays a toast message if no rows are selected or if an error occurs.
     */
    function handleMultipleRecordsEnableDisable() {
        const fun = 'handleMultipleRecordsEnableDisable:';
        try {
            log(fun, 'selectedRowIds:', selectedRowIds);
            if (checkNullArr(selectedRowIds)) {
                if (window.confirm(`Are you sure you want to disable the selected records(${getArrLen(selectedRowIds)})?`)) {
                    handleRecordEnableDisable(selectedRowIds.join(','), true, true, true, true);
                }
            } else {
                toast('Please select some records to delete!', CssVariant.danger);
            }
        } catch (error) {
            toast('Deletion failed, please try again!', CssVariant.danger);
            logErr(fun, error);
        }
    }


    function getModuleColsList(moduleColumns) {
        const resultData = [];
        try {
            for (const key in moduleColumns) {
                if (moduleColumns.hasOwnProperty(key)) {
                    const item = {[key]: moduleColumns[key]};
                    resultData.push(item);
                }
            }
        } catch (error) {
            logErr('getModuleColsList:', error);
        }
        return resultData;
    }

    function getColVisModel(moduleColData) {
        const visModal = {};
        for (const key in moduleColData) {
            const item = moduleColData[key];
            visModal[item.title] = isBoolTrue(getJsonValue(item, "defShow"));
        }
        // log("getColVisModel.visModal:", visModal);
        return visModal;
    }


    function getCsvExportName() {
        const username = (getAdminSessionField('fullname') + '').replaceAll(' ', '_');
        // log('getCsvExportName.username:', username);
        return `${moduleName}_Export_${username}_${getAdminSessionField('userId')}_${formatDate(new Date(), Constants.DD_MMM_YYYY_DASH)}`;
    }

    function toast(text, variant) {
        showToast(text);
        changeToastVariant(variant);
    }

    function log(...text) {
        printLog(TAG, moduleName, ...text);
    }

    function logErr(...text) {
        printError(TAG, moduleName, ...text);
    }

    function getModuleTableStyle() {
        return {
            ".css-1izv73j": {
                width: "auto !important", padding: "0.1rem 0.3rem !important", // minWidth: 'auto !important',
                overflow: "hidden !important",
            },
            ".MuiTablePagination-selectLabel": {
                alignItems: "center !important",
                justifyContent: "center !important",
                display: "flex !important",
                width: "auto !important",
                margin: "0 !important",
                padding: "0 !important",
            },
            ".css-1e7c2qr-MuiToolbar-root-MuiTablePagination-toolbar,.MuiTablePagination-toolbar": {
                width: "auto !important !important",
                justifyContent: "start !important",
                alignItems: "center !important",
                right: "0 !important !important",
                display: "flex !important",
                flexWrap: "nowrap !important",
                flexDirection: "row !important",
            },
            ".MuiTablePagination-displayedRows": {
                marginTop: "1.1rem !important",
            },
            ".MuiDataGrid-footerContainer": {
                justifyContent: "start !important", display: "flex !important",
            },
            ".MuiDataGrid-footerContainer .css-10a9fh2-MuiTablePagination-selectLabel,.MuiDataGrid-footerContainer,.MuiTablePagination-displayedRows,.MuiDataGrid-footerContainer .c,.MuiDataGrid-footerContainer .MuiTablePagination-selectLabel,.MuiDataGrid-footerContainer .MuiSelect-select": {
                fontSize: "12px !important",
            },
            ".MuiToolbar-gutters": {
                marginTop: "0 !important", paddingTop: "0 !important", justifyContent: "start !important",
            },
            ".css-1iyq7zh-MuiDataGrid-columnHeaders": {
                backgroundColor: "#ebebeb !important",
                height: "32px !important",
                maxHeight: "32px !important",
                minHeight: "32px !important",
                paddingTop: ".2rem !important",
                paddingBottom: ".2rem !important",
                borderRadius: "0 !important",
            },
            ".css-y599qu": {
                backgroundColor: "#ebebeb !important",
                height: "32px !important",
                maxHeight: "32px !important",
                minHeight: "32px !important",
                paddingTop: ".2rem !important",
                paddingBottom: ".2rem !important",
                borderRadius: "0 !important",
            },
            ".css-1x7sjdv-MuiButtonBase-root-MuiIconButton-root": {
                transition: "var(--transition) !important",
                fontSize: "1rem !important",
                borderRadius: ".25rem !important",
                padding: ".15rem !important",
                width: "24px !important",
                height: "24px !important",
                margin: ".1rem .5rem !important",
                "&:hover": {backgroundColor: "var(--primary25) !important"},
                "&:focus": {backgroundColor: "var(--primary25) !important"},
                "&:active": {backgroundColor: "var(--primary50) !important"},
            },
            ".css-1ps6pg7-MuiPaper-root": {
                // borderRadius: '15px !important',
                // backgroundColor: 'purple !important',
                boxShadow: '5px 5px 15px rgba(0,0,0,0.5) !important'
            },
            ".css-kk1bwy-MuiButtonBase-root-MuiMenuItem-root": {
                height: '14px !important', // backgroundColor: 'orange !important',
            },
            ".MuiTypography-body1": {
                fontSize: '.75rem !important', // backgroundColor: 'green !important',
            },
            ".MuiListItemText-primary": {
                fontSize: '.75rem !important', // backgroundColor: 'yellow !important',
            },
            ".css-10hburv-MuiTypography-root": {
                fontSize: '.75rem !important', // backgroundColor: 'red !important',
            },
            ".css-yb0lig": {
                fontSize: '.75rem !important', // backgroundColor: 'red !important',
            },
        }
    }
});


export default ModuleTable;
